// ANGULAR
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
// SERVICES
import { AlertService } from '@services/alert.service';
import { GlobalService } from '@services/global.service';
import { GruposService } from '@api-requests-services/grupos.service';
// MODELS
import { BooleanGroupValues, Group } from '@models/grupos.model';

export enum Views {
  Home = 'home',
  Groups = 'groups',
  GroupDetails = 'groupDetails',
  CreatingGroup = 'creatingGroup',
  EditingGroup = 'editingGroup',
}

@Component({
  selector: 'app-grupos',
  templateUrl: './grupos.component.html',
  styleUrl: './grupos.component.css',
  imports: [
    CommonModule,
    FormsModule
  ]
})
export class GruposComponent {
  allGroups: Group[];
  groups: Group[];
  newGroup: Group;
  searchedGroup: Group | null;
  selectedGroup: Group;
  groupNameToSearch: string;
  groupToSearch: string;
  // Control de contenido html
  views = Views;
  groupNotFound: boolean = false;
  isReadOnly: boolean = true;
  currentView: string = Views.Home;
  booleanGroupValues: BooleanGroupValues;

  constructor(
    private alertService: AlertService,
    public globalService: GlobalService,
    private gruposService: GruposService,
    private title: Title
  ) {
    this.title.setTitle('Oráculo - Grupos');

    this.booleanGroupValues = {
      loginGrupo: { activated: false, value: false },
      pruebas: { activated: false, value: false },
      replicacionContinua: { activated: false, value: false }
    };
  }

  getGroups(changeView: boolean): void {
    this.gruposService.getGroups().subscribe(
      groups => {
        if (groups) {
          // console.log('Groups: ', groups);
          this.allGroups = groups;
          this.groups = this.allGroups;
          if (changeView) {
            this.currentView = Views.Groups;
          }
        }
      },
      error => {
        if (error.error === this.globalService.editModeDisabledMsg) {
          this.alertService.swalEditingNotEnabled();
        } else {
          this.alertService.swalError(error.error);
          console.error('Error getting Groups:', error);
        }
      }
    );
  }

  createGroup(): void {
    this.setBooleanDataInNewGroup();

    this.gruposService.postCreateGroup(this.newGroup).subscribe(
      () => {
        this.selectedGroup = this.newGroup;
        this.getGroups(false);
        this.selectView(Views.GroupDetails);
        this.alertService.showAutoCloseMessage(`¡Grupo creado!`, ``, "success", 1200);
      },
      error => {
        if (error.error === this.globalService.editModeDisabledMsg) {
          this.alertService.swalEditingNotEnabled();
        } else {
          this.alertService.swalError(error.error);
          console.error('Error creating Group:', error);
        }
      }
    );
  }

  getGroup(): void {
    this.searchedGroup = { nombre: '' };

    if (this.groupNameToSearch) {
      this.gruposService.getGroup(this.groupNameToSearch).subscribe(
        group => {
          if (group) {
            // console.log('Group: ', group);
            this.groupNotFound = false;
            this.searchedGroup = group;
            this.allGroups = [group];
          }
        },
        error => {
          if (error.error === this.globalService.editModeDisabledMsg) {
            this.alertService.swalEditingNotEnabled();
          } else {
            if (error.status === 404) {
              this.groupNotFound = true;
            } else {
              this.groupNotFound = false;
              this.alertService.swalError(error.error);
            }
            console.error('Error getting Group:', error);
          }
        }
      );
    } else {
      this.groupNotFound = true;
    }
  }

  editGroup(): void {
    this.alertService.showConfirmAlert(
      `Editar Grupo`,
      `¿Seguro que quieres editar la Grupo con nombre "${this.selectedGroup?.nombre ?? ''}"?`
    ).then((result) => {
      if (result.isConfirmed && !!this.selectedGroup) {
        // this.setBooleanDataInNewGroup();
        this.gruposService.putEditGroup(this.selectedGroup.nombre, this.newGroup).subscribe(
          () => {
            this.alertService.showAutoCloseMessage(`¡Grupo editado!`, ``, "success", 1200);
            this.getGroups(true);
            this.selectView(Views.Groups);
          },
          error => {
            if (error.error === this.globalService.editModeDisabledMsg) {
              this.alertService.swalEditingNotEnabled();
            } else {
              this.alertService.swalError(error.error);
              console.error('Error editing Group:', error);
            }
          }
        );
      }
    });
  }

  deleteGroup(): void {
    this.alertService.showConfirmAlert(
      `Eliminar Grupo`,
      `¿Seguro que quieres eliminar el Grupo con nombre "${this.selectedGroup?.nombre ?? ''}"?`
    ).then((result) => {
      if (result.isConfirmed && !!this.selectedGroup) {
        this.gruposService.deleteGroup(this.selectedGroup.nombre).subscribe(
          () => {
            this.alertService.showAutoCloseMessage(`¡Grupo borrado!`, ``, "success", 1200);
            this.getGroups(true);
          },
          error => {
            if (error.error === this.globalService.editModeDisabledMsg) {
              this.alertService.swalEditingNotEnabled();
            } else {
              this.alertService.swalError(error.error);
              console.error('Error deleting Group:', error);
            }
          }
        );
      }
    });
  }

  selectView(view: string, selectedGroup?: Group): void {
    if (selectedGroup) { this.selectedGroup = selectedGroup; }
    this.currentView = view;

    switch (this.currentView) {
      case Views.Home:
        this.searchedGroup = null;
        this.groupNameToSearch = '';
        this.groupToSearch = '';
        this.isReadOnly = true;
        break;
      case Views.Groups:
        this.groupToSearch = '';
        this.groups = this.allGroups;
        this.isReadOnly = true;
        break;
      case Views.GroupDetails:
        this.groupToSearch = '';
        this.isReadOnly = true;
        if (!!this.selectedGroup) {
          this.newGroup = {
            nombre: this.selectedGroup.nombre,
            wsEditorContenidos: this.selectedGroup.wsEditorContenidos,
            wsNovaSmartPhone: this.selectedGroup.wsNovaSmartPhone,
            wsGestion: this.selectedGroup.wsGestion,
            urlEpda: this.selectedGroup.urlEpda,
            loginGrupo: this.selectedGroup.loginGrupo,
            pruebas: this.selectedGroup.pruebas,
            grupoMaestro: this.selectedGroup.grupoMaestro,
            aeReferencia: this.selectedGroup.aeReferencia,
            replicacionContinua: this.selectedGroup.replicacionContinua
          };
        }
        this.initializeBooleanGroupValues(this.newGroup);
        break;
      case Views.CreatingGroup:
        this.initializeBooleanGroupValues({ nombre: '' });
        this.newGroup = { nombre: '' };
        this.isReadOnly = true;
        break;
      case Views.EditingGroup:
        this.isReadOnly = false;
        break;
    }
  }

  initializeBooleanGroupValues(group: Group): void {
    if (group.loginGrupo !== undefined) {
      this.booleanGroupValues.loginGrupo.activated = true;
      this.booleanGroupValues.loginGrupo.value = group.loginGrupo;
    } else {
      this.booleanGroupValues.loginGrupo.activated = false;
      this.booleanGroupValues.loginGrupo.value = false;
    }

    if (group.pruebas !== undefined) {
      this.booleanGroupValues.pruebas.activated = true;
      this.booleanGroupValues.pruebas.value = group.pruebas;
    } else {
      this.booleanGroupValues.pruebas.activated = false;
      this.booleanGroupValues.pruebas.value = false;
    }

    if (group.replicacionContinua !== undefined) {
      this.booleanGroupValues.replicacionContinua.activated = true;
      this.booleanGroupValues.replicacionContinua.value = group.replicacionContinua;
    } else {
      this.booleanGroupValues.replicacionContinua.activated = false;
      this.booleanGroupValues.replicacionContinua.value = false;
    }
  }

  changeActivated(booleanVar: string): void {
    switch (booleanVar) {
      case 'loginGrupo':
        this.booleanGroupValues.loginGrupo.activated = !this.booleanGroupValues.loginGrupo.activated;
        break;
      case 'pruebas':
        this.booleanGroupValues.pruebas.activated = !this.booleanGroupValues.pruebas.activated;
        break;
      case 'replicacionContinua':
        this.booleanGroupValues.replicacionContinua.activated = !this.booleanGroupValues.replicacionContinua.activated;
        break;
    }
  }

  setBooleanDataInNewGroup(): void {
    if (this.booleanGroupValues.loginGrupo.activated) {
      this.newGroup.loginGrupo = this.booleanGroupValues.loginGrupo.value;
    } else {
      this.newGroup.loginGrupo = undefined;
    }

    if (this.booleanGroupValues.pruebas.activated) {
      this.newGroup.pruebas = this.booleanGroupValues.pruebas.value;
    } else {
      this.newGroup.pruebas = undefined;
    }

    if (this.booleanGroupValues.replicacionContinua.activated) {
      this.newGroup.replicacionContinua = this.booleanGroupValues.replicacionContinua.value;
    } else {
      this.newGroup.replicacionContinua = undefined;
    }
  }

  onSearchChange(): void {
    this.groups = this.allGroups.filter(secret => secret.nombre.toLowerCase().includes(this.groupToSearch.toLowerCase()));

    if (this.groupToSearch === undefined || this.groupToSearch === '' || this.groupToSearch === ' ') {
      this.groups = this.allGroups;
    }
  }
}